import { IWorkspaceAction } from '../../@types/workspaceTypes';
import React, { ReactElement, ReactNode, useContext, useState } from 'react';
import DocumentModal2 from '../../components/modals/DocumentModal2';
import { ConsolidatedPick, PickingInstruction } from '../../graphql/entities';
import WorkspaceContext from '../../components/Context/WorkspaceContext';
import { useBackgroundMutation } from '../../utils/BackgroundJobs';
import ConsolidatedPicksPrintMutation from '../../graphql/mutations/sales/ConsolidatedPicksPrintMutation.graphql';
import ConsolidatedPicksIndividualPrintMutation from '../../graphql/mutations/sales/ConsolidatedPicksIndividualPrintMutation.graphql';
import { onDocumentComplete } from '../../utils/DocumentUtils';
import { useActionCriteria } from '../../utils/useActionProps';
import { useDispatch } from 'react-redux';
import {
  IConsolidatedPicksIndividualPrintMutationResult,
  IConsolidatedPicksIndividualPrintMutationVariables,
  IConsolidatedPicksPrintMutationResult,
  IConsolidatedPicksPrintMutationVariables,
} from '../../graphql/schema/p4types';
import getClientMutationId from '../../utils/getClientMutationId';
import normalizeVariables from '../../graphql/schema/normalizeVariables';
import { useDispatchAction } from '../../components/ActionButtons/dispatch/DispatchAction';
import { DispatchSource } from '../../components/ActionButtons/dispatch/DispatchModal';

enum ConsolidatedPickWorkspaceAction {
  Print = 'Print',
  PrintIndividual = 'Print Picking Instructions',
}

export const workspaceActionsForPermissions: Array<{ actionReference: ConsolidatedPickWorkspaceAction }> = [
  {
    actionReference: ConsolidatedPickWorkspaceAction.Print,
  },
  {
    actionReference: ConsolidatedPickWorkspaceAction.PrintIndividual,
  },
];

export const useConsolidatedPickWorkspaceActions = (): [Array<IWorkspaceAction>, ReactNode | ReactElement] => {
  const actionCriteria = useActionCriteria();
  const dispatch = useDispatch();

  const [printModalOpen, setPrintModalOpen] = useState(false);
  const [individualPrintModalOpen, setIndividualPrintModalOpen] = useState(false);

  const { selectionState } = useContext(WorkspaceContext);

  const [dispatchActionButton, dispatchPrintModals] = useDispatchAction({
    source: DispatchSource.ConsolidatedPickWorkspace,
  });

  const [consolidatedPicksPrint] = useBackgroundMutation<
    IConsolidatedPicksPrintMutationResult,
    IConsolidatedPicksPrintMutationVariables
  >(ConsolidatedPicksPrintMutation, {
    onCompleteJob: (job) => {
      onDocumentComplete({
        documentName: 'Print Consolidated Pick',
        dispatch,
        job,
      });
    },
  });

  const [individualPicksPrint] = useBackgroundMutation<
    IConsolidatedPicksIndividualPrintMutationResult,
    IConsolidatedPicksIndividualPrintMutationVariables
  >(ConsolidatedPicksIndividualPrintMutation, {
    onCompleteJob: (job) => {
      onDocumentComplete({
        documentName: 'Print Individual Picking Instructions',
        dispatch,
        job,
      });
    },
  });

  const actions: Array<IWorkspaceAction> = [
    {
      key: 'consolidatedPicksPrint',
      text: 'Print',
      onClick: () => setPrintModalOpen(true),
      actionReference: ConsolidatedPickWorkspaceAction.Print,
    },
    {
      key: 'consolidatedPicksIndividualPrint',
      text: 'Print Picking Instructions',
      onClick: () => setIndividualPrintModalOpen(true),
      actionReference: ConsolidatedPickWorkspaceAction.PrintIndividual,
    },
    dispatchActionButton,
  ];

  const modals = (
    <>
      <DocumentModal2
        modalOpen={printModalOpen}
        entity={ConsolidatedPick}
        title="Print Consolidated Picks"
        modalWrapper={true}
        count={selectionState.count}
        onClose={() => setPrintModalOpen(false)}
        onPrintClick={({ reportParameters }) => {
          consolidatedPicksPrint({
            variables: {
              input: {
                actionCriteria: normalizeVariables(actionCriteria, [
                  'uniqueCarriers',
                  'salesOrderCount',
                  'itemCount',
                  'pickedItemCount',
                  'toPickItemCount',
                ]),
                clientMutationId: getClientMutationId(),
                reportParameters: normalizeVariables(reportParameters),
              },
            },
          });
        }}
      />
      <DocumentModal2
        modalOpen={individualPrintModalOpen}
        entity={PickingInstruction}
        title="Print Picking Instructions"
        modalWrapper={true}
        count={selectionState.count}
        onClose={() => setIndividualPrintModalOpen(false)}
        onPrintClick={({ reportParameters }) => {
          individualPicksPrint({
            variables: {
              input: {
                actionCriteria: normalizeVariables(actionCriteria, [
                  'uniqueCarriers',
                  'salesOrderCount',
                  'itemCount',
                  'pickedItemCount',
                  'toPickItemCount',
                ]),
                clientMutationId: getClientMutationId(),
                reportParameters: normalizeVariables(reportParameters),
              },
            },
          });
        }}
      />
      {dispatchPrintModals}
    </>
  );

  return [actions, modals];
};
